import { ActionIcon, Badge, Button, Code, Group, Stack, Switch, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { IconPin, IconPinFilled, IconTrash, IconX } from '@tabler/icons-react';
import Config from 'src/common/Config';
import { FilterHeader } from 'src/common/components/table/SearchHeader';
import { Flags, useFlags } from 'src/feature-flags/FlagsHook';
import useFlagOverrides from 'src/feature-flags/useFlagOverrides';
import usePinnedFlags from 'src/feature-flags/usePinnedFlags';

type FlagRow = {
  id: string;
  name: string;
  value: boolean;
  isOverridden: boolean;
  isPinned: boolean;
};

export default function FeatureFlagsTab() {
  const flags = useFlags();
  const { overrides, setOverride, clearOverride, clearAll, hasOverrides } = useFlagOverrides();
  const { togglePin, isPinned } = usePinnedFlags();
  const [searchTerm, setSearchTerm] = useState('');
  const isDev = Config.isDev();

  const rows: FlagRow[] = useMemo(() => {
    const allRows = (Object.keys(flags) as (keyof Flags)[]).map(key => ({
      id: key,
      name: key,
      value: flags[key],
      isOverridden: isDev && key in overrides,
      isPinned: isPinned(key),
    }));

    return allRows.sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [flags, overrides, isDev, isPinned]);

  const filtered = useMemo(() => {
    if (!searchTerm) return rows;
    const lower = searchTerm.toLowerCase();
    return rows.filter(r => r.name.toLowerCase().includes(lower));
  }, [rows, searchTerm]);

  const columns: DataTableColumn<FlagRow>[] = useMemo(
    () => [
      {
        accessor: 'pin',
        title: '',
        width: 40,
        hidden: !isDev,
        render: (row: FlagRow) => (
          <ActionIcon
            variant="subtle"
            color={row.isPinned ? 'brand' : 'gray'}
            size="sm"
            onClick={() => togglePin(row.name as keyof Flags)}
          >
            {row.isPinned ? <IconPinFilled size={16} /> : <IconPin size={16} />}
          </ActionIcon>
        ),
      },
      {
        accessor: 'name',
        title: 'Flag name',
        render: (row: FlagRow) => (
          <Code fw={row.isOverridden ? 700 : undefined} c={row.isOverridden ? 'brand' : undefined}>
            {row.name}
          </Code>
        ),
      },
      {
        accessor: 'value',
        title: 'Value',
        width: 140,
        render: (row: FlagRow) =>
          isDev ? (
            <Group gap="xs">
              <Switch
                checked={row.value}
                onChange={() => setOverride(row.name as keyof Flags, !row.value)}
                size="sm"
              />
              {row.isOverridden && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => clearOverride(row.name as keyof Flags)}
                >
                  <IconX size={14} />
                </ActionIcon>
              )}
            </Group>
          ) : (
            <Badge color={row.value ? 'green' : 'gray'} variant="light">
              {String(row.value)}
            </Badge>
          ),
      },
    ],
    [isDev, setOverride, clearOverride, togglePin],
  );

  return (
    <Stack gap="sm">
      {isDev && (
        <Text size="sm" c="dimmed">
          Overrides are stored in local storage and only affect the frontend in this browser. Pin
          flags to toggle them from the navbar.
        </Text>
      )}
      <Group justify="space-between">
        <FilterHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          inputProps={{ autoFocus: true }}
          placeholder="Search flags"
        />
        {isDev && hasOverrides && (
          <Button
            variant="subtle"
            color="red"
            size="sm"
            leftSection={<IconTrash size={16} />}
            onClick={clearAll}
          >
            Discard local overrides
          </Button>
        )}
      </Group>

      <DataTable<FlagRow>
        withTableBorder={false}
        withColumnBorders={false}
        withRowBorders
        borderColor="gray.3"
        rowBorderColor="gray.3"
        striped
        highlightOnHover
        columns={columns}
        records={filtered}
        minHeight={200}
        styles={{
          header: {
            backgroundColor: 'var(--mantine-color-gray-0)',
            fontSize: 'var(--mantine-font-size-xs)',
            fontWeight: 600,
            color: 'var(--mantine-color-gray-6)',
          },
        }}
      />
    </Stack>
  );
}
